import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ListSkeleton } from '@/components/ui/Skeleton';
import { usePlatform } from '@/providers/PlatformProvider';
import { useFormErrors } from '@/hooks/useFormErrors';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import {
  accountKeys,
  createAddress,
  deleteAddress,
  fetchAddresses,
  makeAddressDefault,
  updateAddress,
} from '@/api/account';
import type { Address, AddressPayload } from '@/types/account';

/**
 * Address book — BUILD-PLAN §16 (multiple shipping addresses), §43.
 *
 * The list and the form are one screen rather than two routes. An address
 * book is edited in short bursts — fix a postcode, promote a different
 * default — and a full navigation between each edit turns a ten-second task
 * into a page load and a scroll back to where you were.
 *
 * Country rules come from platform configuration, so the postcode label and
 * whether a region is required follow the destination rather than assuming
 * everywhere works like the UK (§43).
 */
export function AddressesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Address | 'new' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  usePageMetadata({ title: 'Addresses' });

  const { data: addresses = [], isPending, isError } = useQuery({
    queryKey: accountKeys.addresses,
    queryFn: fetchAddresses,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: accountKeys.addresses });

  const removal = useMutation({
    mutationFn: (id: number) => deleteAddress(id),
    onSuccess: () => {
      setNotice('Address removed.');
      void refresh();
    },
  });

  const promotion = useMutation({
    mutationFn: (id: number) => makeAddressDefault(id),
    onSuccess: () => {
      setNotice('Default address updated.');
      void refresh();
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="My account"
        title="Addresses"
        description="Where we send your orders. The default is offered first at checkout."
        className="mb-8"
        actions={
          editing === null ? (
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              Add address
            </Button>
          ) : null
        }
      />

      {notice && (
        <div className="mb-6">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {editing !== null && (
        <div className="mb-10">
          <AddressForm
            address={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={(message) => {
              setEditing(null);
              setNotice(message);
              void refresh();
            }}
          />
        </div>
      )}

      {isError ? (
        <Alert tone="error">
          We could not load your addresses. Please refresh and try again.
        </Alert>
      ) : isPending ? (
        <ListSkeleton rows={2} />
      ) : addresses.length === 0 ? (
        /*
         | An empty state that DOES something. "No addresses" alone leaves the
         | customer to work out that the button above is what they want (§86).
         */
        <div className="border border-line bg-surface px-6 py-14 text-center">
          <MapPin
            className="mx-auto h-6 w-6 text-accent"
            strokeWidth={1.5}
            aria-hidden="true"
          />

          <h2 className="mt-4 font-display text-xl text-ink">No saved addresses yet</h2>

          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted">
            Save an address and it will be offered at checkout, so you only ever type it once.
          </p>

          {editing === null && (
            <div className="mt-6">
              <Button onClick={() => setEditing('new')}>Add your first address</Button>
            </div>
          )}
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {addresses.map((address) => (
            <li key={address.id}>
              <article className="flex h-full flex-col border border-line bg-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-display text-lg text-ink">
                      {address.label || `${address.firstName} ${address.lastName}`}
                    </h2>

                    {address.isDefault && (
                      <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-2.5 py-0.5 text-[0.6875rem] uppercase tracking-[0.16em] text-accent">
                        <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                        Default
                      </span>
                    )}
                  </div>
                </div>

                {/*
                  `lines` is formatted server-side in the destination's own
                  conventions — the order of city, region and postcode differs
                  by country and is not the client's to guess (§43).
                */}
                <address className="mt-4 flex-1 text-sm not-italic leading-[1.9] text-muted">
                  {(address.lines ?? []).map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </address>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(address)}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    Edit
                  </Button>

                  {!address.isDefault && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={promotion.isPending && promotion.variables === address.id}
                        onClick={() => promotion.mutate(address.id)}
                      >
                        Make default
                      </Button>

                      {/*
                        Removal is offered only on non-default addresses.
                        Deleting the default leaves checkout with nothing to
                        pre-fill and no obvious replacement; promoting another
                        first makes the intent explicit.
                      */}
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={removal.isPending && removal.variables === address.id}
                        onClick={() => {
                          if (window.confirm('Remove this address?')) {
                            removal.mutate(address.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */

function AddressForm({
  address,
  onCancel,
  onSaved,
}: {
  address: Address | null;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { config } = usePlatform();
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  /*
   | The store's OWN market, not whatever sorts first alphabetically.
   |
   | Defaulting to `countries[0]` selected Argentina on a British shop, so
   | every customer had to hunt through sixty options to correct a field that
   | should already have been right. Matching on the base trading currency
   | finds the home market without hard-coding a country code, so this stays
   | correct if the store is ever configured for another market.
   */
  const homeCountry =
    config.countries.find((entry) => entry.currency === config.commerce.currency) ??
    config.countries[0];

  const [values, setValues] = useState<AddressPayload>({
    label: address?.label ?? '',
    first_name: address?.firstName ?? '',
    last_name: address?.lastName ?? '',
    company: address?.company ?? '',
    phone: address?.phone ?? '',
    line_1: address?.line1 ?? '',
    line_2: address?.line2 ?? '',
    city: address?.city ?? '',
    region: address?.region ?? '',
    postcode: address?.postcode ?? '',
    country_code: address?.countryCode ?? homeCountry?.code ?? 'GB',
  });

  const country = config.countries.find((entry) => entry.code === values.country_code);

  const mutation = useMutation({
    mutationFn: () => {
      /*
       | Blank optionals are sent as null, not "". The API stores what it is
       | given, and an empty string is a value — it would render as a blank
       | line in the formatted address.
       */
      const payload: AddressPayload = {
        ...values,
        label: values.label?.trim() || null,
        company: values.company?.trim() || null,
        phone: values.phone?.trim() || null,
        line_2: values.line_2?.trim() || null,
        region: values.region?.trim() || null,
        postcode: values.postcode?.trim() || null,
      };

      return address ? updateAddress(address.id, payload) : createAddress(payload);
    },
    onSuccess: () => {
      reset();
      onSaved(address ? 'Address updated.' : 'Address saved.');
    },
    onError: handleError,
  });

  const set = (key: keyof AddressPayload) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  return (
    <section className="border border-line bg-surface p-6 sm:p-7">
      <div className="flex items-center gap-3 border-b border-line pb-3">
        <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
        <h2 className="font-display text-xl text-ink">
          {address ? 'Edit address' : 'New address'}
        </h2>
      </div>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
        className="mt-6"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            label="Label"
            value={values.label ?? ''}
            hint="Optional — “Home”, “Office”."
            error={fieldErrors['label']}
            onChange={(event) => set('label')(event.target.value)}
          />

          <span className="hidden sm:block" aria-hidden="true" />

          <Input
            label="First name"
            value={values.first_name}
            required
            autoComplete="given-name"
            error={fieldErrors['first_name']}
            onChange={(event) => set('first_name')(event.target.value)}
          />

          <Input
            label="Last name"
            value={values.last_name}
            required
            autoComplete="family-name"
            error={fieldErrors['last_name']}
            onChange={(event) => set('last_name')(event.target.value)}
          />

          <Input
            label="Company"
            value={values.company ?? ''}
            autoComplete="organization"
            error={fieldErrors['company']}
            onChange={(event) => set('company')(event.target.value)}
          />

          <Input
            label="Phone"
            type="tel"
            value={values.phone ?? ''}
            autoComplete="tel"
            hint="Couriers use this for delivery updates."
            error={fieldErrors['phone']}
            onChange={(event) => set('phone')(event.target.value)}
          />

          <div className="sm:col-span-2">
            {/* Select takes `options` and hands back the VALUE, not the
                event — see its own note on why. */}
            <Select
              label="Country"
              value={values.country_code}
              required
              error={fieldErrors['country_code']}
              options={config.countries.map((entry) => ({
                value: entry.code,
                label: entry.name,
              }))}
              onChange={set('country_code')}
            />
          </div>

          <div className="sm:col-span-2">
            <Input
              label="Address line 1"
              value={values.line_1}
              required
              autoComplete="address-line1"
              error={fieldErrors['line_1']}
              onChange={(event) => set('line_1')(event.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <Input
              label="Address line 2"
              value={values.line_2 ?? ''}
              autoComplete="address-line2"
              error={fieldErrors['line_2']}
              onChange={(event) => set('line_2')(event.target.value)}
            />
          </div>

          <Input
            label="Town / City"
            value={values.city}
            required
            autoComplete="address-level2"
            error={fieldErrors['city']}
            onChange={(event) => set('city')(event.target.value)}
          />

          {/*
            Region and postcode follow the DESTINATION's rules, taken from
            platform configuration: their label, whether they are required,
            and whether they exist at all. Ireland and the UAE have no
            universal postcode, and demanding one makes the form unusable
            there (§43).
          */}
          <Input
            label={country?.region?.label ?? 'County / State'}
            value={values.region ?? ''}
            required={country?.region?.required ?? false}
            autoComplete="address-level1"
            error={fieldErrors['region']}
            onChange={(event) => set('region')(event.target.value)}
          />

          {country?.postcode !== null && (
            <Input
              label={country?.postcode?.label ?? 'Postcode'}
              value={values.postcode ?? ''}
              required={country?.postcode?.required ?? false}
              autoComplete="postal-code"
              {...(country?.postcode?.example
                ? { hint: `For example ${country.postcode.example}` }
                : {})}
              error={fieldErrors['postcode']}
              onChange={(event) => set('postcode')(event.target.value)}
            />
          )}
        </div>

        {formError && (
          <div className="mt-5">
            <Alert tone="error">{formError}</Alert>
          </div>
        )}

        <div className="mt-7 flex flex-wrap gap-3">
          <Button type="submit" loading={mutation.isPending}>
            {address ? 'Save changes' : 'Save address'}
          </Button>

          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
